'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { logger } from '@/lib/logger';
import { IProduct } from '@/services/types';

export type ProductVariant = {
  id?: string | number;
  has_stock?: boolean | null;
  stock_initial?: number | null;
  stock_reserved?: number | null;
  stock_sold?: number | null;
  price_public_value?: number | null;
  status?: string;
  sku?: string | null;
  color?: { name: string; hex?: string; value?: string };
  size?: { name: string; order?: number };
  images?: any[];
};

type ProductVariantSelectorProps = {
  product: IProduct & { id: string };
  onVariantChange?: (variant: ProductVariant | null) => void;
};

/**
 * Variant selector component for products.
 * main purpose is to allow users to select different variants of a product (e.g., size, color).
 * select the desired variant add quantity before adding the product to the cart.
 * -----------------------------------
 * product data comes with variants information.
 * {
 *  "variants": []
 *  "colors": []
 *  "sizes": []
 * }
 * we can use this data to build a variant selector UI.
 * ----------------------------------
 * Business logic:
 * ----------------------------------
 * - display available options for each variant attribute (e.g., color swatches, size dropdown).
 * - when a user selects an option, update the selected variant state.
 * - ensure that only valid combinations of variants can be selected.
 * - provide feedback if a selected variant is out of stock.
 * - if the product has no variants, the component can simply return a message indicating that there are no variants available.
 * - if the field variants is empty or undefined, use colors and sizes to build variant options.
 * - if both variants and colors/sizes are undefined or empty, return null.
 * - only call onSelect with the selected variant when a combination is made.
 * - combination of variant is required to call onSelect.
 * ----------------------------------
 *
 * @param param0
 * @returns
 */
const ProductVariantSelector = ({ product, onVariantChange }: ProductVariantSelectorProps) => {
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);

  // Reset selection when the product changes (e.g., navigating between products)
  useEffect(() => {
    setSelectedColor(null);
    setSelectedSize(null);
  }, [product.id]);

  // If no variant and no colors/sizes, return null
  if (!product.variants && !product.colors?.length && !product.sizes?.length) {
    logger.warn('ProductVariantSelector: No variant, colors, or sizes available for this product.');
    return null;
  }

  // Normalize + memoize variants (prefer backend-provided variants).
  const variants = useMemo(() => {
    const normalize = (v: ProductVariant) => {
      const colorRaw: any = (v as any).color;
      const sizeRaw: any = (v as any).size;
      const color = colorRaw
        ? {
            name: colorRaw.name || colorRaw.value || String(colorRaw),
            hex: colorRaw.hex || colorRaw.value || undefined,
            value: colorRaw.value || undefined,
          }
        : undefined;
      const size = sizeRaw
        ? {
            name: sizeRaw.name || sizeRaw.value || String(sizeRaw),
            order: (sizeRaw as any).order,
          }
        : undefined;
      return { ...v, color, size } as ProductVariant;
    };

    let list = [] as ProductVariant[];

    if (product.variants) {
      list = product.variants.map((v) => normalize(v as any));
    }

    if (!list.length && product.colors && product.sizes) {
      list = product.colors.flatMap((color) => product.sizes!.map((size) => ({ color, size, status: 'available', has_stock: true }) as ProductVariant));
    }

    return list;
  }, [product.variants, product.colors, product.sizes]);

  const colors = useMemo(() => {
    // When real variants exist, derive colors from them — they are the ground truth.
    // product.colors may be out of sync with the colors actually linked on variants.
    if (variants.length > 0) {
      const seen = new Set<string>();
      const result: { name: string; hex?: string }[] = [];
      for (const v of variants) {
        if (v.color && !seen.has(v.color.name)) {
          seen.add(v.color.name);
          result.push({ name: v.color.name, hex: v.color.hex });
        }
      }
      if (result.length > 0) return result;
    }
    // No variants or variants have no colors — fall back to product.colors.
    if (product.colors && product.colors.length) return product.colors.map((c) => ({ name: c.name, hex: c.hex }));
    return [];
  }, [product.colors, variants]);

  const sizes = useMemo(() => {
    const list = product.sizes && product.sizes.length ? product.sizes : (variants.map((v) => v.size).filter(Boolean) as { name: string; order?: number }[]);
    return list.slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  }, [product.sizes, variants]);

  // Function to check if a variant is available.
  // A variant is available only when has_stock is true AND stock_initial > 0.
  const isVariantAvailable = (variant: ProductVariant) => {
    if (variant.has_stock !== true) return false;
    if (typeof variant.stock_initial === 'number' && variant.stock_initial <= 0) return false;
    return true;
  };

  // Auto-select the first available color + size when nothing is selected yet
  // (including immediately after a product reset). This fires whenever selections
  // are both null, which also covers the product-change reset effect above.
  useEffect(() => {
    if (selectedColor !== null || selectedSize !== null) return;
    if (colors.length === 0 && sizes.length === 0) return;

    const firstColor = colors.find((c) => variants.some((v) => v.color?.name === c.name && isVariantAvailable(v)));
    const firstSize = sizes.find((s) =>
      firstColor ? variants.some((v) => v.size?.name === s.name && v.color?.name === firstColor.name && isVariantAvailable(v)) : variants.some((v) => v.size?.name === s.name && isVariantAvailable(v)),
    );

    if (firstColor) setSelectedColor(firstColor.name);
    if (firstSize) setSelectedSize(firstSize.name);
  }, [colors, sizes, variants, selectedColor, selectedSize]);

  // Get selected variant (match against normalized variant attributes)
  const getSelectedVariant = (): ProductVariant | null => {
    if (!selectedColor || !selectedSize) return null;
    return (
      variants.find(
        (v) => (v.color?.name === selectedColor || (v.color as any)?.value === selectedColor) && (v.size?.name === selectedSize || (v.size as any)?.value === selectedSize) && isVariantAvailable(v),
      ) ?? null
    );
  };

  const selectedVariant = useMemo(() => getSelectedVariant(), [variants, selectedColor, selectedSize]);

  // Avoid emitting parent updates when identity/key hasn't changed (prevents render loops)
  const lastEmittedVariantKey = useRef<string | null>(null);
  const variantKey = (v: ProductVariant | null) => {
    if (!v) return null;
    return v.id ?? `${v.color?.name ?? ''}::${(v.color as any)?.value ?? ''}::${v.size?.name ?? ''}::${(v.size as any)?.value ?? ''}::${v.stock_initial ?? ''}`;
  };

  useEffect(() => {
    const key = variantKey(selectedVariant);
    if (onVariantChange && key !== lastEmittedVariantKey.current) {
      lastEmittedVariantKey.current = key as string;
      onVariantChange(selectedVariant);
      logger.debug({ message: 'ProductVariantSelector: Selected variant changed', variant: selectedVariant });
    }
  }, [selectedVariant, onVariantChange]);

  useEffect(() => {
    logger.debug({
      msg: 'ProductVariantSelector: Component rendered',
      productId: product.id,
      availableVariants: variants,
      availableColors: colors.length,
      availableSizes: sizes,
    });
  }, [product, variants, colors, sizes]);

  // If no variants after processing, show message
  if (!variants.length) {
    logger.warn('ProductVariantSelector: No hay variantes disponibles después del procesamiento.');
    return <div className="text-gray-500 text-sm">No hay variantes disponibles.</div>;
  }

  return (
    <div className="space-y-4">
      {/* Color Selector */}
      {colors.length > 0 && (
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">Color:</label>
          <div className="flex gap-2">
            {colors.map((color, key) => {
              const isSelected = selectedColor === color.name;
              // If a size is already chosen, only enable colors that pair with it.
              const hasAvailableVariant = selectedSize
                ? variants.some((v) => v.color?.name === color.name && v.size?.name === selectedSize && isVariantAvailable(v))
                : variants.some((v) => v.color?.name === color.name && isVariantAvailable(v));
              return (
                <div className="flex flex-col justify-center items-center gap-2" key={color.name + key}>
                  <button
                    className={`w-10 h-10 rounded-full border-2 transition-all ${
                      isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-gray-300 hover:border-gray-400'
                    } ${!hasAvailableVariant ? 'opacity-50 cursor-not-allowed border-dashed border-gray-300' : 'cursor-pointer hover:scale-110'}`}
                    style={{ backgroundColor: (color as any).hex || (color as any).value || color.name }}
                    onClick={() => hasAvailableVariant && setSelectedColor(color.name)}
                    disabled={!hasAvailableVariant}
                    title={color.name}
                  />
                  <div className="text-xs text-gray-600">{!color.hex && color.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Size Selector */}
      {sizes.length > 0 && (
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">Talla:</label>
          <div className="flex gap-2 flex-wrap">
            {sizes.map((size, key) => {
              const isSelected = selectedSize === size.name;
              // If a color is already chosen, only enable sizes that pair with it.
              const hasAvailableVariant = selectedColor
                ? variants.some((v) => v.size?.name === size.name && v.color?.name === selectedColor && isVariantAvailable(v))
                : variants.some((v) => v.size?.name === size.name && isVariantAvailable(v));
              return (
                <button
                  key={size.name + key}
                  className={`px-4 py-2 border rounded-lg font-medium transition-all ${
                    isSelected ? 'border-primary bg-primary text-white' : 'border-gray-300 text-gray-700 hover:border-primary hover:bg-primary/10'
                  } ${!hasAvailableVariant ? 'opacity-50 cursor-not-allowed border-dashed' : 'cursor-pointer'}`}
                  onClick={() => hasAvailableVariant && setSelectedSize(size.name)}
                  disabled={!hasAvailableVariant}
                >
                  {size.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Feedback if selected combination is not available */}
      {selectedColor && selectedSize && !selectedVariant && (
        <div className="text-error text-sm bg-error/10 border border-error/20 rounded-lg px-3 py-2">La combinación seleccionada no está disponible.</div>
      )}
    </div>
  );
};

export default ProductVariantSelector;
